import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'src/app/hooks';
import { setCurrentModal } from 'src/app/reducers/modal-states.reducer';
import { ModalType } from 'src/common/enums';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { PaymentPendingType } from 'src/features/payment-pending/payment-pending.constants';

import { useChargebeeJS } from '../use-chargebee-js';

import { useGenerateCheckoutUrlMutation } from './use-chargebee-checkout.slice';
import {
  type BusinessCheckoutPayload,
  type BusinessSubscriptionPayload,
  type CertificationExamCheckoutPayload,
  CheckoutType,
  type GenerateCheckoutPayload,
  type PersonalSubscriptionPayload,
} from './use-chargebee-checkout.types';

interface Config {
  getPath: (params: string) => string;
  pendingType: PaymentPendingType;
}

const checkoutConfig: Record<CheckoutType, Config> = {
  [CheckoutType.SUBSCRIPTION]: {
    getPath: (params) => `generate-checkout-url${params}`,
    pendingType: PaymentPendingType.PremiumSubscription,
  },
  [CheckoutType.BUSINESS_SUBSCRIPTION]: {
    getPath: () => 'create-self-serve-checkout-url',
    pendingType: PaymentPendingType.SetupBusinessSubscription,
  },
  [CheckoutType.CLOUD_ADDON]: {
    getPath: () => 'generate-cloud-addon-checkout-url',
    pendingType: PaymentPendingType.CloudAddon,
  },
  [CheckoutType.BUSINESS_CHECKOUT]: {
    getPath: () => 'generate-business-checkout-url',
    pendingType: PaymentPendingType.SetupBusinessCheckout,
  },

  [CheckoutType.CERTIFICATION_EXAM]: {
    getPath: () => 'generate-certification-exam-checkout-url',
    pendingType: PaymentPendingType.CertificationExam,
  },
};

const isBusinessCheckout = (payload: GenerateCheckoutPayload | undefined): payload is BusinessSubscriptionPayload =>
  !!(payload as BusinessSubscriptionPayload)?.companyId;

export const useChargebeeCheckout = (
  type: CheckoutType,
  skipNavigate = false,
  onSuccess = () => {},
): {
  isLoading: boolean;
  openCheckout: ((payload?: GenerateCheckoutPayload) => void) | undefined;
} => {
  const { getPath, pendingType } = checkoutConfig[type];

  const [trackEvent] = useTrackEventMutation();
  const isScriptLoading = useChargebeeJS();
  const navigate = useNavigate();
  const [generateCheckoutUrl] = useGenerateCheckoutUrlMutation();

  const { isLoading: isUserSelfLoading, isSuccess: isAuth } = useGetUserQuery();

  const isLoading = isScriptLoading || isUserSelfLoading;

  const dispatch = useAppDispatch();
  const openRSModal = () => dispatch(setCurrentModal(ModalType.RESTRICTED_SERVICE));

  if (isLoading) return { isLoading, openCheckout: undefined };

  const openCheckout = (payload?: GenerateCheckoutPayload) => {
    const params = new URLSearchParams();
    const campaign = (payload as PersonalSubscriptionPayload)?.campaign;

    const enabledCheckoutTypes = new Set([CheckoutType.SUBSCRIPTION, CheckoutType.CERTIFICATION_EXAM]);

    if (enabledCheckoutTypes.has(type) && campaign) {
      params.set('campaign', campaign);
    }

    const paramsStr = params.toString();
    const path = getPath(paramsStr ? `?${paramsStr}` : '');

    if (type !== CheckoutType.BUSINESS_CHECKOUT && !isAuth) navigate('/login');
    if (!window.Chargebee) throw new Error('Chargebee script loaded but not available');

    const chargebeeInstance = window.Chargebee.getInstance();
    chargebeeInstance.openCheckout({
      loaded: () => trackEvent({ event: 'chargebee-checkout-open' }),
      close: () => trackEvent({ event: 'chargebee-checkout-cancel' }),
      step: (step) => trackEvent({ event: 'chargebee-checkout-step', properties: { step } }),
      hostedPage: () =>
        generateCheckoutUrl({ path, payload })
          .unwrap()
          // we must handle the error while also returning a rejecting promise
          // so we must use .catch() for chargebee checkout to close without the error propagating
          .catch((error?: { data?: { message?: string }; status?: number }) => {
            const restrictedErrorMessages = ['Unauthorized', 'Forbidden'];
            const errorMessage = error?.data?.message ?? 'Something went wrong';

            if (error?.status === 403 && restrictedErrorMessages.includes(errorMessage)) {
              openRSModal();
            } else {
              NotificationDispatch('error', errorMessage);
            }

            trackEvent({ event: 'chargebee-checkout-error', properties: { error: errorMessage } });
          }),
      success: () => {
        trackEvent({ event: 'chargebee-checkout-success' });
        chargebeeInstance.closeAll();
        const search = createSearchParams({
          t: pendingType,
          ...(isBusinessCheckout(payload) && { id: payload.companyId }),
          ...(pendingType === PaymentPendingType.SetupBusinessCheckout && {
            email: (payload as BusinessCheckoutPayload).meta.email,
          }),
          ...(pendingType === PaymentPendingType.CertificationExam && {
            certificationExamType: (payload as CertificationExamCheckoutPayload).certificationExamType,
            itemPriceId: (payload as CertificationExamCheckoutPayload).itemPriceId,
          }),
        }).toString();
        if (!skipNavigate) {
          navigate({
            pathname: '/payment/pending',
            search,
          });
        }
        onSuccess();
      },
    });
  };

  return { isLoading, openCheckout };
};
